import { isEqual, uniqWith } from 'lodash';
import { componentInterface } from '@ardoq/component-interface';
import { isTruthy } from '@ardoq/common-helpers';
import { getIcon } from '@ardoq/icons';
import { filterInterface } from '@ardoq/filter-interface';
import {
  getGraphInterfaceWithModelInterfaces,
  getLegendComponentTypeColor,
} from '@ardoq/graph';
import {
  APIComponentType,
  APIOrganizationUser,
  FormattingFilter,
  isSystemFilter,
} from '@ardoq/api-types';
import { parseConditionalFormatting } from './conditionalFormattingTextFormatter';
import {
  ActiveConditionalFormattingForLegend,
  ComponentTypeForLegend,
  ConditionalFormattingInfo,
} from './types';
import { EnhancedScopeData } from '@ardoq/data-model';
import { buildMinimalScopeDataForFilters } from './buildMinimalScopeDataForFilters';
import { type I18nLocale } from '@ardoq/i18n';

export const componentTypeToLegendItem = (
  type: APIComponentType
): ComponentTypeForLegend => {
  if (type.image) {
    return {
      label: type.name,
      color: getLegendComponentTypeColor(type),
      representationData: {
        isImage: true,
        value: type.image || '',
      },
    };
  }
  return {
    label: type.name,
    color: getLegendComponentTypeColor(type),
    representationData: {
      value: null,
      isImage: false,
      icon: getIcon(type.icon),
      shapeName: type.shape,
    },
  };
};

export const getComponentTypesForLegend = (
  componentIds: string[]
): ComponentTypeForLegend[] => {
  return uniqWith(
    [
      ...new Set(componentIds.map(componentInterface.getType).filter(isTruthy)),
    ].map(componentTypeToLegendItem),
    isEqual
  );
};

const getFiltersByCategories = (filters: ConditionalFormattingInfo[]) => ({
  components: filters.filter(filter => filter.filterType === 'components'),
  references: filters.filter(filter => filter.filterType === 'references'),
  tags: filters.filter(filter => filter.filterType === 'tags'),
});

export const getActiveConditionalFormattingForLegend = (
  i18nLocale: I18nLocale,
  formattingFilters?: FormattingFilter[],
  isNewConditionalFormatting?: boolean,
  users?: APIOrganizationUser[]
): ActiveConditionalFormattingForLegend => {
  const activeFilters =
    formattingFilters ?? filterInterface.getFormattingFilters();

  const enhancedScopeDataForLegend = buildMinimalScopeDataForFilters(
    getGraphInterfaceWithModelInterfaces(),
    activeFilters,
    users
  );

  return getActiveConditionalFormattingForLegendPure(
    enhancedScopeDataForLegend,
    activeFilters,
    i18nLocale,
    Boolean(isNewConditionalFormatting)
  );
};

const haveSameVisualRepresentation = (
  a: ConditionalFormattingInfo,
  b: ConditionalFormattingInfo
): boolean =>
  a.filterType === b.filterType &&
  a.color?.toLowerCase() === b.color?.toLowerCase() &&
  a.filterDescription === b.filterDescription;

const deduplicateLegendItems = (
  items: ConditionalFormattingInfo[]
): ConditionalFormattingInfo[] => uniqWith(items, haveSameVisualRepresentation);

export const getActiveConditionalFormattingForLegendPure = (
  scopeData: EnhancedScopeData,
  activeFormattingFilters: FormattingFilter[],
  i18nLocale: I18nLocale,
  isNewConditionalFormatting: boolean
) => {
  const filters = activeFormattingFilters
    // exclude system filters and filters without color, as they are not relevant to be shown in the legend
    .filter(filter => filter.color && !isSystemFilter(filter))
    .flatMap(filter =>
      parseConditionalFormatting(
        scopeData,
        filter,
        i18nLocale,
        isNewConditionalFormatting
      )
    );

  const deduplicated = deduplicateLegendItems(filters);

  return getFiltersByCategories(deduplicated);
};
